import React, { useState, useEffect, useRef } from 'react';
import { Home, Mail, Users } from 'lucide-react';

const ContactPage = ({ content, language }) => {
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  
  // Refs for scroll-triggered animations
  const heroRef = useRef(null);
  const contactSectionRef = useRef(null);
  const formRef = useRef(null);
  const mapRef = useRef(null);
  
  // Visibility states for animations
  const [heroVisible, setHeroVisible] = useState(false);
  const [contactSectionVisible, setContactSectionVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  
  // Create fallback content in case properties are missing
  const contactContent = content?.[language]?.contact || {
    title: 'Get In Touch',
    name: 'Name',
    email: 'Email',
    message: 'Message',
    send: 'Send Message',
    address: '123 Design Avenue, Creative District, CA 90210',
    phone: '+1 (555) 123-4567',
    emailAddress: 'contact@example.com',
    addressTitle: 'Address',
    emailTitle: 'Email',
    phoneTitle: 'Phone',
    intro: {
      paragraph1: "Thank you for your interest in my services. My mission is to inspire you through my journey while sharing my knowledge in business, real estate, and personal growth.",
      paragraph2: "I invite you to follow me on my social media platforms to stay updated on my daily projects and upcoming endeavors."
    }
  };
  
  useEffect(() => {
    // Immediately show the hero section
    setHeroVisible(true);
    
    // Function to check if element is in viewport
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return (
        rect.top <= (window.innerHeight - offset) && 
        rect.bottom >= offset
      );
    };
    
    // Function to handle scroll events
    const handleScroll = () => {
      if (contactSectionRef.current && isInViewport(contactSectionRef.current)) {
        setContactSectionVisible(true);
      }
      
      if (formRef.current && isInViewport(formRef.current)) {
        setFormVisible(true);
      }
      
      if (mapRef.current && isInViewport(mapRef.current)) {
        setMapVisible(true);
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Initial check on page load
    handleScroll();
    
    // Cleanup function
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const handleContactSubmit = () => {
    console.log('Form submitted:', { contactName, contactEmail, contactMessage });
    setContactName('');
    setContactEmail('');
    setContactMessage('');
    alert('Message sent successfully!');
  };
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Banner with Image Background */}
      <div 
        ref={heroRef}
        className="relative h-96 overflow-hidden"
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black z-10"></div>
        
        {/* Background image (would be replaced with your actual image) */}
        <div className="absolute inset-0 bg-gray-800 bg-opacity-70">
          {/* This would be where you'd place your image */}
          {/* <img src="/path/to/your/image.jpg" className="w-full h-full object-cover" alt="Zeus Hernandez" /> */}
        </div>
        
        {/* Hero content */}
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center z-20 px-6">
          <h1 
            className={`text-5xl font-bold mb-4 tracking-wider transition-all duration-700 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          >
            {language === 'en' ? 'LETS TALK' : 'HABLEMOS'}
          </h1>
          <h2 
            className={`text-4xl font-bold italic mb-6 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          >
            {language === 'en' ? 'BUSINESS' : 'NEGOCIOS'}
          </h2>
          <p 
            className={`text-lg max-w-md mx-auto transition-all duration-700 delay-500 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          >
            {language === 'en' 
              ? "Let's share stories" 
              : "Compartamos historias"}
          </p>
          <button 
            className={`mt-8 border border-white px-6 py-2 hover:bg-white hover:text-black transition-all duration-700 delay-700 ${heroVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          >
            {language === 'en' ? 'LEARN MORE' : 'CONOCE MÁS'}
          </button>
        </div>
      </div>
      
      {/* Main Contact Section */}
      <div 
        ref={contactSectionRef}
        className="max-w-6xl mx-auto px-6 md:px-12 py-16"
      >
        {/* Section Title with Glow Effect */}
        <h2 
          className={`text-5xl font-bold mb-12 text-center tracking-wider relative transition-all duration-700 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
        >
          <span className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]">
            {language === 'en' ? 'CONTACT ME' : 'CONTÁCTAME'}
          </span>
        </h2>
        
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left side - Intro Text */}
          <div className="space-y-8">
            <p 
              className={`text-gray-300 leading-relaxed transition-all duration-700 delay-300 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
            >
              {language === 'en' 
                ? contactContent.intro?.paragraph1 || "Thank you for your interest in my services. My mission is to inspire you through my journey while sharing my knowledge in business, real estate, and personal growth."
                : "Gracias por tu interés en mis servicios. Mi misión es inspirarte a través de mi trayectoria mientras comparto mi experiencia en negocios, bienes raíces y desarrollo personal."
              }
            </p>
            <p 
              className={`text-gray-300 leading-relaxed transition-all duration-700 delay-500 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
            >
              {language === 'en'
                ? contactContent.intro?.paragraph2 || "I invite you to follow me on my social media platforms to stay updated on my daily projects and upcoming endeavors."
                : "Te invito a seguirme en mis redes sociales para mantenerte al tanto de mis proyectos diarios y próximas emprendimientos."
              }
            </p>
            
            {/* Contact Info */}
            <div className="space-y-6 mt-12">
              <div 
                className={`transition-all duration-700 delay-700 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
              >
                <h3 className="text-xl mb-2 flex items-center">
                  <Home size={20} className="mr-2" /> {contactContent.addressTitle || 'Address'}
                </h3>
                <p className="text-gray-300 ml-7">
                  {contactContent.address}
                </p>
              </div>
              <div 
                className={`transition-all duration-700 delay-800 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
              >
                <h3 className="text-xl mb-2 flex items-center">
                  <Mail size={20} className="mr-2" /> {contactContent.emailTitle || 'Email'}
                </h3>
                <p className="text-gray-300 ml-7">
                  {contactContent.emailAddress || contactContent.email}
                </p>
              </div>
              <div 
                className={`transition-all duration-700 delay-900 ${contactSectionVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
              >
                <h3 className="text-xl mb-2 flex items-center">
                  <Users size={20} className="mr-2" /> {contactContent.phoneTitle || 'Phone'}
                </h3>
                <p className="text-gray-300 ml-7">
                  {contactContent.phone}
                </p>
              </div>
            </div>
          </div>
          
          {/* Right side - Contact Form */}
          <div 
            ref={formRef}
            className={`bg-gray-900 bg-opacity-30 p-8 rounded-lg transition-all duration-700 ${formVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-x-10'}`}
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm mb-2">
                  {language === 'en' ? 'Name*' : 'Nombre*'}
                </label>
                <input 
                  type="text" 
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-white transition-colors duration-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-2">
                  {language === 'en' ? 'Email Address*' : 'Correo Electrónico*'}
                </label>
                <input 
                  type="email" 
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-white transition-colors duration-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-2">
                  {language === 'en' ? 'Phone Number*' : 'Teléfono*'}
                </label>
                <input 
                  type="tel"
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-white transition-colors duration-300"
                />
              </div>
              <div>
                <label className="block text-sm mb-2">
                  {language === 'en' ? 'Message*' : 'Mensaje*'}
                </label>
                <textarea 
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-white h-32 transition-colors duration-300"
                  required
                />
              </div>
              <button 
                onClick={handleContactSubmit}
                className="px-6 py-3 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors duration-300"
              >
                {language === 'en' ? 'Submit' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Map Section */}
      <div 
        ref={mapRef}
        className={`w-full h-96 bg-gray-900 mt-16 transition-all duration-1000 ${mapVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        {/* Map would go here */}
        <div className="h-full flex items-center justify-center text-gray-500">
          <p>Map placeholder</p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;